import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { Input, Textarea, Label, Select } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Sheet, Muted } from "@/components/ui/overlays";
import { useToast } from "@/app/Toast";
import { useAuth } from "@/app/AuthContext";
import { cn, formatDateTime, formatMoney } from "@/lib/utils";
import { ArrowLeft, Download, Receipt, ShieldCheck, Banknote, RotateCcw } from "lucide-react";
import { minorDigitsFor, parseMajorToMinor } from "../../shared/money";

interface ItemEntry {
  id: number; kind: "SERVICE" | "ADDON" | "TOPUP";
  name: string; durationMinutes: number; unitPriceCents: number; quantity: number;
  lineTotalCents: number;
}
interface PaymentEntry {
  id: number; amountCents: number; currency: string; method: string; status: string;
  bookingAmountCents: number; bookingCurrency: string; fxRate: string | null;
  fxRateSource: string | null; fxRateAsOf: string | null; entryType: "PAYMENT" | "REFUND";
  externalReference: string | null; notes: string | null; recordedAt: string;
  originalPaymentId: number | null;
}
interface RefundOption {
  id: number; amountCents: number; currency: string; method: string; status: string; recordedAt: string;
  refundedAmountCents: number; pendingAmountCents: number; remainingRefundableCents: number;
}
interface AuditEntry {
  id: number; action: string; entityType: string; entityId: string | null;
  beforeJson: unknown; afterJson: unknown; createdAt: string;
  requestId?: string | null;
}
interface BookingDetail {
  id: number; code: string; status: string; source: string; serviceMode: string; version: number;
  notes: string | null;
  serviceAddress: { line1: string | null; line2: string | null; city: string | null; region: string | null; postalCode: string | null; countryCode: string | null };
  travel: { timeMinutes: number | null; distanceMeters: number | null };
  startAt: string; endAt: string; timezone: string;
  customer: { id: number; firstName: string; lastName: string; email: string | null; phone: string | null };
  vehicle: { id: number; plate: string; make: string | null; model: string | null } | null;
  resource: { id: number; name: string; type: string };
  resources: Array<{ id: number; name: string; type: string; role: "PRIMARY" | "STAFF" }>;
  location: { id: number; name: string; timezone: string; currency: string };
  total: { amount: number; currency: string; unit: "minor" };
  billing: { snapshotId: number | null; subtotalMinor: number; discountType: "NONE" | "FIXED" | "PERCENTAGE"; discountValue: number; discountMinor: number; membershipBenefitDiscountMinor: number; taxRateBps: number; taxMinor: number; totalMinor: number };
  membershipBenefits: Array<{ id: number; benefitKey: string; type: string; amountMinor: number; quantity: number; itemName: string | null; membershipName: string; released: boolean; periodStartsAt: string; periodEndsAt: string }>;
  paymentStatus: string;
  timeline: {
    confirmed: string | null; checkedIn: string | null; inProgress: string | null;
    completed: string | null; cancelled: string | null; cancelledReason: string | null;
    noShow: string | null;
  };
  items: ItemEntry[];
  payments: PaymentEntry[];
  paidCents: number;
  audit: AuditEntry[];
}
interface ReassignVehicle { id: number; plate: string; make: string | null; model: string | null; archivedAt: string | null; }
interface ReassignCustomer { id: number; firstName: string; lastName: string; email: string | null; phone: string | null; }

const STATUS_FLOW: Array<{ key: keyof BookingDetail["timeline"]; next?: string; label: string }> = [
  { key: "confirmed", next: "CHECKED_IN", label: "Confirmed" },
  { key: "checkedIn", next: "IN_PROGRESS", label: "Checked in" },
  { key: "inProgress", next: "COMPLETED", label: "In progress" },
  { key: "completed", label: "Completed" },
];

export function BookingDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const [data, setData] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<string | null>(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPreview, setCancelPreview] = useState<{ fee: number; within: boolean } | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundOptions, setRefundOptions] = useState<RefundOption[]>([]);
  const [refundOptionsLoading, setRefundOptionsLoading] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  const refresh = () => api.get<BookingDetail>(`/api/v1/bookings/${id}/detail`)
    .then((result) => { setData(result); setError(null); })
    .catch((e) => setError(e.message))
    .finally(() => setLoading(false));

  useEffect(() => {
    void refresh();
  }, [id]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading booking…</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">No booking.</p>;

  async function transition(next: string) {
    setTransitioning(next);
    try {
      await api.post(`/api/v1/bookings/${id}/status`, { status: next, version: data!.version });
      toast("success", `Marked ${next}`);
      await refresh();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Transition failed");
    } finally {
      setTransitioning(null);
    }
  }

  async function doCancel() {
    try {
      await api.post(`/api/v1/bookings/${id}/cancel`, { version: data!.version });
      toast("success", cancelPreview && cancelPreview.fee > 0 ? `Cancelled. Fee of ${formatMoney(cancelPreview.fee, data!.total.currency)} recorded.` : "Cancelled");
      await refresh();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Cancel failed");
    }
  }

  async function previewCancel() {
    try {
      const result = await api.get<{ feeCents: number; withinWindow: boolean; hoursUntilStart: number }>(`/api/v1/bookings/${id}/cancel-preview`);
      setCancelPreview({ fee: result.feeCents, within: result.withinWindow });
      setCancelOpen(true);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Preview failed");
    }
  }

  const canTransitionTo = (next: string) => {
    if (data.status === "PENDING") return next === "CONFIRMED";
    const map: Record<string, string[]> = {
      CONFIRMED: ["CHECKED_IN", "CANCELLED", "NO_SHOW"],
      CHECKED_IN: ["IN_PROGRESS", "CANCELLED"],
      IN_PROGRESS: ["COMPLETED"],
      COMPLETED: [],
      CANCELLED: [],
      NO_SHOW: [],
    };
    return (map[data.status] ?? []).includes(next);
  };

  const canTopUp = ["CHECKED_IN", "IN_PROGRESS", "COMPLETED"].includes(data.status);
  const canTip = canTopUp;
  const canCancel = ["CONFIRMED", "CHECKED_IN"].includes(data.status);

  async function openRefundRequest() {
    setRefundOptionsLoading(true);
    setRefundOpen(true);
    try {
      const result = await api.get<{ payments: RefundOption[] }>(`/api/v1/bookings/${data!.id}/refund-options`);
      setRefundOptions(result.payments);
    } catch (e) {
      setRefundOpen(false);
      toast("error", e instanceof Error ? e.message : "Could not load refundable payments");
    } finally {
      setRefundOptionsLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => nav(-1)} className="rounded-md p-1 hover:bg-accent" aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-2xl font-semibold tracking-tight">{data.code}</h1>
        <Badge>{data.status}</Badge>
        {data.serviceMode === "MOBILE" && <Badge>Mobile</Badge>}
        <Badge>{data.paymentStatus}</Badge>
        <Link to={`/wash-execution/${data.id}`} className="ml-2 text-sm font-medium text-primary hover:underline">Wash execution</Link>
        <span className="ml-auto text-sm text-muted-foreground">
          {formatDateTime(data.startAt)} → {formatDateTime(data.endAt)}
        </span>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Items</CardTitle>
                <span className="text-sm tabular-nums">{formatMoney(data.total.amount, data.total.currency)} total</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kind</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead className="text-right">Unit</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead className="text-right">Line total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>
                        <span className={cn(
                          "rounded px-2 py-0.5 text-[10px] font-semibold uppercase",
                          i.kind === "SERVICE" && "bg-sky-100 text-sky-900",
                          i.kind === "ADDON" && "bg-purple-100 text-purple-900",
                          i.kind === "TOPUP" && "bg-amber-100 text-amber-900",
                        )}>{i.kind}</span>
                      </TableCell>
                      <TableCell>{i.name}</TableCell>
                      <TableCell>{i.durationMinutes > 0 ? `${i.durationMinutes}m` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMoney(i.unitPriceCents, data.total.currency)}</TableCell>
                      <TableCell>{i.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMoney(i.lineTotalCents, data.total.currency)}</TableCell>
                    </TableRow>
                  ))}
                  {data.items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No items.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="ml-auto grid max-w-sm gap-1 border-t pt-3 text-sm tabular-nums">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(data.billing.subtotalMinor, data.total.currency)}</span></div>
                {data.billing.membershipBenefitDiscountMinor > 0 && <div className="flex justify-between text-emerald-700"><span>Membership savings</span><span>-{formatMoney(data.billing.membershipBenefitDiscountMinor, data.total.currency)}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Manual discount {data.billing.discountType !== "NONE" ? `(${data.billing.discountType.toLowerCase()})` : ""}</span><span>-{formatMoney(Math.max(0, data.billing.discountMinor - data.billing.membershipBenefitDiscountMinor), data.total.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax ({(data.billing.taxRateBps / 100).toFixed(2)}%)</span><span>{formatMoney(data.billing.taxMinor, data.total.currency)}</span></div>
                <div className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><span>{formatMoney(data.billing.totalMinor, data.total.currency)}</span></div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <a href={`/api/v1/bookings/${data.id}/documents/invoice`} target="_blank" rel="noreferrer"><Button type="button" size="sm" variant="outline">Invoice</Button></a>
                <a href={`/api/v1/bookings/${data.id}/documents/receipt`} target="_blank" rel="noreferrer"><Button type="button" size="sm" variant="outline">Receipt</Button></a>
                {hasRole("MANAGER", "ADMIN") && <Button type="button" size="sm" variant="outline" onClick={() => setBillingOpen(true)}>Adjust discount / tax</Button>}
              </div>
              {canTopUp && (
                <RecordTopUp bookingId={data.id} version={data.version} onToppedUp={refresh} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Payments</CardTitle>
              <Muted>
                Paid {formatMoney(data.paidCents, data.total.currency)} of {formatMoney(data.total.amount, data.total.currency)}.
                Status <strong>{data.paymentStatus}</strong>.
              </Muted>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex flex-wrap justify-end gap-2">
                {hasRole("STAFF", "MANAGER", "ADMIN") && data.status !== "CANCELLED" && (
                  <Button size="sm" variant="outline" onClick={() => void openRefundRequest()}>
                    <RotateCcw className="h-3.5 w-3.5" /> Request refund
                  </Button>
                )}
                <Button size="sm" onClick={() => setPaymentOpen(true)}><Banknote className="h-3.5 w-3.5" /> Record payment</Button>
              </div>
              {data.payments.length === 0 && <p className="text-sm text-muted-foreground">No payments recorded yet.</p>}
              {data.payments.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payments.map((p) => {
                      const refunded = p.entryType === "PAYMENT"
                        ? -data.payments.filter((refund) => refund.entryType === "REFUND" && refund.originalPaymentId === p.id)
                          .reduce((sum, refund) => sum + refund.amountCents, 0)
                        : 0;
                      return (
                        <TableRow key={p.id}>
                          <TableCell>{formatDateTime(p.recordedAt)}</TableCell>
                          <TableCell className="tabular-nums">
                            <div>{formatMoney(p.amountCents, p.currency)}</div>
                            {p.currency !== p.bookingCurrency && (
                              <div className="text-xs text-muted-foreground">
                                {formatMoney(p.bookingAmountCents, p.bookingCurrency)} @ {p.fxRate}
                              </div>
                            )}
                          </TableCell>
                          <TableCell><Badge>{p.method}</Badge></TableCell>
                          <TableCell>
                            <div>{p.entryType === "REFUND" ? "REFUND" : p.status}</div>
                            {p.entryType === "PAYMENT" && refunded > 0 && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {formatMoney(refunded, p.currency)} refunded
                                {refunded < p.amountCents ? " (partial)" : ""}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{p.externalReference ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{p.notes ?? "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {canTip && (
                <div className="mt-3 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setTipOpen(true)}>
                    <Banknote className="h-3.5 w-3.5" /> Record tip / gratuity
                  </Button>
                  <Muted>Tips are recorded as a TOPUP line item called "Tip" and are included in the booking total.</Muted>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Audit trail</CardTitle>
              <Muted>Most recent {data.audit.length} entries.</Muted>
            </CardHeader>
            <CardContent>
              {data.audit.length === 0 && <p className="text-sm text-muted-foreground">No audit entries.</p>}
              {data.audit.length > 0 && (
                <ul className="space-y-2 text-sm">
                  {data.audit.map((a) => (
                    <li key={a.id} className="rounded border bg-muted/30 px-3 py-2">
                      <p className="font-medium">{a.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(a.createdAt)}
                        {a.requestId ? ` · req ${String(a.requestId).slice(0, 8)}` : ""}
                      </p>
                      {a.afterJson != null && (
                        <details>
                          <summary className="cursor-pointer text-xs text-muted-foreground">after</summary>
                          <pre className="mt-1 max-h-60 overflow-auto rounded bg-background p-2 font-mono text-[11px]">
{JSON.stringify(a.afterJson, null, 2)}
                          </pre>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4" /> Status flow</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm">
                {STATUS_FLOW.map((step) => {
                  const ts = data.timeline[step.key];
                  return (
                    <li key={step.key} className="flex items-center gap-3">
                      <div className={cn(
                        "h-2 w-2 rounded-full",
                        ts ? "bg-emerald-500" : "bg-muted-foreground/30",
                      )} />
                      <span className={cn(ts ? "" : "text-muted-foreground")}>{step.label}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{ts ? formatDateTime(ts) : "—"}</span>
                    </li>
                  );
                })}
                {(data.timeline.cancelled || data.timeline.noShow) && (
                  <li className="border-t pt-2 text-xs text-muted-foreground">
                    {data.timeline.cancelled && <>Cancelled {formatDateTime(data.timeline.cancelled)}{data.timeline.cancelledReason ? ` — ${data.timeline.cancelledReason}` : ""}</>}
                    {data.timeline.noShow && <>Marked no-show {formatDateTime(data.timeline.noShow)}</>}
                  </li>
                )}
              </ol>
              <div className="mt-3 flex flex-wrap gap-2">
                {["CHECKED_IN", "IN_PROGRESS", "COMPLETED", "NO_SHOW"].map((next) =>
                  canTransitionTo(next) ? (
                    <Button
                      key={next}
                      size="sm"
                      variant={next === "NO_SHOW" ? "destructive" : "outline"}
                      disabled={transitioning === next}
                      onClick={() => void transition(next)}
                    >
                      → {next}
                    </Button>
                  ) : null,
                )}
                {canCancel && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void previewCancel()}
                  >
                    Cancel booking…
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Customer</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p><Link to={`/customers/${data.customer.id}`} className="font-medium hover:underline">{data.customer.firstName} {data.customer.lastName}</Link></p>
              <p className="text-muted-foreground">{data.customer.email ?? "—"}{data.customer.phone ? ` · ${data.customer.phone}` : ""}</p>
              {!(["COMPLETED", "CANCELLED", "NO_SHOW"].includes(data.status)) && <Button size="sm" variant="outline" onClick={() => setReassignOpen(true)}>Change customer / vehicle</Button>}
            </CardContent>
          </Card>

          {data.vehicle && (
            <Card>
              <CardHeader><CardTitle className="text-base">Vehicle</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="font-mono text-base">{data.vehicle.plate}</p>
                <p className="text-muted-foreground">{data.vehicle.make ?? "—"} {data.vehicle.model ?? ""}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">{data.serviceMode === "MOBILE" ? "Mobile team" : "Bay"} & Location</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {data.resources.map((resource) => (
                <p key={resource.id} className="flex items-center gap-2">
                  <span className="font-medium">{resource.name}</span>
                  <Badge>{resource.role === "PRIMARY" ? resource.type : "STAFF"}</Badge>
                </p>
              ))}
              <p className="text-muted-foreground">{data.location.name} · {data.location.timezone}</p>
              {data.serviceMode === "MOBILE" && (
                <>
                  {data.serviceAddress.line1 ? (
                    <div className="rounded border bg-muted/30 p-3 text-xs">
                      <p className="font-medium">{data.serviceAddress.line1}</p>
                      {data.serviceAddress.line2 && <p>{data.serviceAddress.line2}</p>}
                      <p>
                        {[data.serviceAddress.city, data.serviceAddress.region, data.serviceAddress.postalCode]
                          .filter(Boolean).join(", ")}
                        {data.serviceAddress.countryCode && ` · ${data.serviceAddress.countryCode}`}
                      </p>
                    </div>
                  ) : <p className="text-xs text-muted-foreground">No service address on file.</p>}
                  <p className="text-xs text-muted-foreground">
                    Travel: {data.travel.timeMinutes != null ? `${data.travel.timeMinutes}m` : "—"}
                    {data.travel.distanceMeters != null && data.travel.distanceMeters > 0
                      ? ` · ${(data.travel.distanceMeters / 1000).toFixed(1)} km`
                      : ""}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {data.notes && (
            <Card>
              <CardHeader><CardTitle className="text-base">Notes</CardTitle></CardHeader>
              <CardContent><p className="whitespace-pre-wrap text-sm">{data.notes}</p></CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Download className="h-4 w-4" /> Calendar</CardTitle></CardHeader>
            <CardContent>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void (async () => {
                    const r = await fetch(`/api/v1/bookings/${data.code}/ics`, { credentials: "same-origin" });
                    if (!r.ok) { toast("error", "ICS download failed"); return; }
                    const t = await r.text();
                    const blob = new Blob([t], { type: "text/calendar" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = `${data.code}.ics`; a.click();
                    URL.revokeObjectURL(url);
                  })();
                }}
              >
                <Download className="h-3.5 w-3.5" /> Download .ics
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {tipOpen && (
        <RecordTipSheet
          bookingId={data.id}
          currency={data.total.currency}
          onClose={() => setTipOpen(false)}
          onSaved={() => { setTipOpen(false); void refresh(); }}
        />
      )}
      {paymentOpen && (
        <RecordPaymentSheet
          bookingId={data.id}
          bookingCurrency={data.total.currency}
          onClose={() => setPaymentOpen(false)}
          onSaved={() => { setPaymentOpen(false); void refresh(); }}
        />
      )}
      {billingOpen && (
        <BillingAdjustmentSheet
          bookingId={data.id}
          billing={data.billing}
          settledMinor={data.paidCents}
          onClose={() => setBillingOpen(false)}
          onSaved={() => { setBillingOpen(false); void refresh(); }}
        />
      )}
      {refundOpen && (
        <RefundRequestSheet
          bookingId={data.id}
          payments={refundOptions}
          loading={refundOptionsLoading}
          onClose={() => setRefundOpen(false)}
          onSaved={() => { setRefundOpen(false); void refresh(); }}
        />
      )}
      {reassignOpen && (
        <ReassignPartySheet
          booking={data}
          onClose={() => setReassignOpen(false)}
          onSaved={() => { setReassignOpen(false); void refresh(); }}
        />
      )}

      {cancelOpen && cancelPreview && (
        <Sheet open onClose={() => setCancelOpen(false)} title="Cancel booking?">
          <div className="space-y-3">
            {cancelPreview.within && cancelPreview.fee > 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                A cancellation fee of <strong>{formatMoney(cancelPreview.fee, data.total.currency)}</strong> will be charged.
              </div>
            ) : (
              <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                This cancellation is free of charge.
              </div>
            )}
            <Muted>The booking will be marked CANCELLED and any slot reservations released.</Muted>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCancelOpen(false)}>Keep booking</Button>
              <Button
                type="button"
                variant={cancelPreview.fee > 0 ? "destructive" : "default"}
                onClick={async () => { await doCancel(); setCancelOpen(false); }}
              >
                Confirm cancellation
              </Button>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
}

function ReassignPartySheet({ booking, onClose, onSaved }: {
  booking: BookingDetail; onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const [query, setQuery] = useState(`${booking.customer.firstName} ${booking.customer.lastName}`);
  const [customers, setCustomers] = useState<ReassignCustomer[]>([]);
  const [customerId, setCustomerId] = useState(booking.customer.id);
  const [vehicles, setVehicles] = useState<ReassignVehicle[]>([]);
  const [vehicleId, setVehicleId] = useState<number | null>(booking.vehicle?.id ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setCustomers([]); return; }
    api.get<ReassignCustomer[]>(`/api/v1/customers?q=${encodeURIComponent(query.trim())}`)
      .then(setCustomers).catch(() => setCustomers([]));
  }, [query]);

  useEffect(() => {
    api.get<ReassignCustomer & { vehicles: ReassignVehicle[] }>(`/api/v1/customers/${customerId}`)
      .then((result) => setVehicles((result.vehicles ?? []).filter((item) => !item.archivedAt)))
      .catch(() => setVehicles([]));
  }, [customerId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/api/v1/bookings/${booking.id}/party`, { customerId, vehicleId, version: booking.version });
      toast("success", "Booking customer and vehicle updated");
      onSaved();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Could not update booking");
    } finally { setSaving(false); }
  }

  return (
    <Sheet open onClose={onClose} title="Change customer / vehicle" description="The booking history remains attached to this booking. Closed bookings cannot be reassigned.">
      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-1.5">
          <Label>Customer</Label>
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, phone, or email" />
          {customers.length > 0 && <div className="divide-y rounded-md border">{customers.map((item) => <button type="button" key={item.id} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => { setCustomerId(item.id); setQuery(`${item.firstName} ${item.lastName}`); setCustomers([]); setVehicleId(null); }}>{item.firstName} {item.lastName} <span className="text-muted-foreground">{item.phone ?? item.email ?? ""}</span></button>)}</div>}
        </div>
        <div className="space-y-1.5">
          <Label>Vehicle</Label>
          <Select value={vehicleId?.toString() ?? ""} onChange={(event) => setVehicleId(event.target.value ? Number(event.target.value) : null)}>
            <option value="">No vehicle</option>
            {vehicles.map((item) => <option key={item.id} value={item.id}>{item.plate} · {[item.make, item.model].filter(Boolean).join(" ")}</option>)}
          </Select>
        </div>
        <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Update booking"}</Button></div>
      </form>
    </Sheet>
  );
}

function RefundRequestSheet({ bookingId, payments, loading, onClose, onSaved }: {
  bookingId: number; payments: RefundOption[]; loading: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const eligible = payments.filter((payment) => payment.remainingRefundableCents > 0);
  const [paymentId, setPaymentId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = payments.find((payment) => payment.id === Number(paymentId));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) { toast("error", "Select a payment"); return; }
    let amountCents: number;
    try {
      amountCents = parseMajorToMinor(amount, selected.currency);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Enter a valid amount"); return;
    }
    const cleanReason = reason.trim();
    if (amountCents <= 0 || amountCents > selected.remainingRefundableCents) {
      toast("error", `Amount must be between ${formatMoney(1, selected.currency)} and ${formatMoney(selected.remainingRefundableCents, selected.currency)}`); return;
    }
    if (cleanReason.length < 3) { toast("error", "Reason must be at least 3 characters"); return; }
    setSaving(true);
    try {
      await api.post("/api/v1/refund-requests", { paymentId: selected.id, amountCents, reason: cleanReason });
      toast("success", "Refund request submitted for review");
      onSaved();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Refund request failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onClose={onClose} title="Request partial refund" description={`Booking #${bookingId}. A manager or admin must approve this request.`}>
      {loading ? <p className="text-sm text-muted-foreground">Loading refundable payments…</p> : (
        <form onSubmit={submit} className="space-y-4">
          {eligible.length === 0 ? (
            <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">No payment has a refundable balance. Pending requests also reserve their requested amounts.</div>
          ) : (
            <>
              <div><Label>Payment</Label><Select required value={paymentId} onChange={(e) => { setPaymentId(e.target.value); setAmount(""); }}>
                <option value="">Select a payment</option>
                {eligible.map((payment) => <option key={payment.id} value={payment.id}>#{payment.id} · {payment.method} · {formatMoney(payment.remainingRefundableCents, payment.currency)} available</option>)}
              </Select></div>
              {selected && <div className="grid grid-cols-3 gap-2 rounded-md border bg-muted/25 p-3 text-xs">
                <div><span className="block text-muted-foreground">Paid</span><strong>{formatMoney(selected.amountCents, selected.currency)}</strong></div>
                <div><span className="block text-muted-foreground">Refunded / pending</span><strong>{formatMoney(selected.refundedAmountCents, selected.currency)} / {formatMoney(selected.pendingAmountCents, selected.currency)}</strong></div>
                <div><span className="block text-muted-foreground">Available</span><strong>{formatMoney(selected.remainingRefundableCents, selected.currency)}</strong></div>
              </div>}
              <div><Label>Refund amount {selected ? `(${selected.currency})` : ""}</Label><Input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={selected && minorDigitsFor(selected.currency) === 0 ? "100" : "100.00"} /></div>
              <div><Label>Reason</Label><Textarea required minLength={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this refund needed?" /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? "Submitting…" : "Submit request"}</Button></div>
            </>
          )}
        </form>
      )}
    </Sheet>
  );
}

function RecordTopUp({ bookingId, version, onToppedUp }: { bookingId: number; version: number; onToppedUp: () => void | Promise<void> }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [qty, setQty] = useState("1");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/api/v1/bookings/${bookingId}/topups`, {
        name,
        unitPriceCents: Number(amount),
        quantity: Number(qty),
        description: desc || null,
        version,
      });
      toast("success", "Top-up recorded");
      setOpen(false); setName(""); setAmount(""); setQty("1"); setDesc("");
      await onToppedUp();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="mt-3 border-t pt-3">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Receipt className="h-3.5 w-3.5" /> Record top-up
        </Button>
        <Muted>Optional: anything billed beyond the original quote. Subtotal auto-updates.</Muted>
      </div>
      <Sheet open={open} onClose={() => setOpen(false)} title="Record top-up" description="Post-service extra. Bumps total + adds a TOPUP line item.">
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label>Description</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Extra clay bar treatment" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unit price (minor units)</Label>
              <Input required type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" min="1" max="99" value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Internal notes</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="optional" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Record"}</Button>
          </div>
        </form>
      </Sheet>
    </>
  );
}

const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="space-y-1.5">{children}</div>;
void FieldRow;

function RecordPaymentSheet({ bookingId, bookingCurrency, onClose, onSaved }: {
  bookingId: number; bookingCurrency: string; onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(bookingCurrency);
  const [method, setMethod] = useState("CASH");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [fxSource, setFxSource] = useState("");
  const [fxAsOf, setFxAsOf] = useState(() => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const settlementCurrency = currency.trim().toUpperCase();
  const crossCurrency = settlementCurrency !== bookingCurrency;

  async function save(event: FormEvent) {
    event.preventDefault();
    let amountMinor: number;
    try {
      amountMinor = parseMajorToMinor(amount, settlementCurrency);
      if (amountMinor <= 0) throw new RangeError("Amount must be greater than zero");
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Invalid amount");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/v1/bookings/${bookingId}/payments`, {
        amountCents: amountMinor,
        currency: settlementCurrency,
        method,
        externalReference: reference || null,
        notes: notes || null,
        ...(crossCurrency ? {
          fxRate,
          fxRateSource: fxSource,
          fxRateAsOf: new Date(fxAsOf).toISOString(),
        } : {}),
      }, true, { "Idempotency-Key": idempotencyKey });
      toast("success", "Payment recorded");
      onSaved();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Payment failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onClose={onClose} title="Record payment" description={`Booking balance is tracked in ${bookingCurrency}.`}>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Settlement amount</Label><Input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.00" /></div>
          <div><Label>Settlement currency</Label><Input required maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} /></div>
        </div>
        {crossCurrency && (
          <div className="space-y-3 rounded-md border bg-muted/20 p-3">
            <Muted>Rate direction: 1 {settlementCurrency} equals how many {bookingCurrency}.</Muted>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>FX rate</Label><Input required inputMode="decimal" value={fxRate} onChange={(e) => setFxRate(e.target.value)} placeholder="320.50" /></div>
              <div><Label>Rate source</Label><Input required value={fxSource} onChange={(e) => setFxSource(e.target.value)} placeholder="Central bank / processor" /></div>
            </div>
            <div><Label>Rate observed at</Label><Input required type="datetime-local" value={fxAsOf} onChange={(e) => setFxAsOf(e.target.value)} /></div>
          </div>
        )}
        <div><Label>Method</Label><Select value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="CASH">CASH</option><option value="CARD_OFFLINE">CARD (offline)</option>
          <option value="BANK_TRANSFER">BANK TRANSFER</option><option value="WALLET">WALLET</option><option value="OTHER">OTHER</option>
        </Select></div>
        <div><Label>External reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} /></div>
        <div><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Record payment"}</Button></div>
      </form>
    </Sheet>
  );
}

function BillingAdjustmentSheet({ bookingId, billing, settledMinor, onClose, onSaved }: {
  bookingId: number; billing: BookingDetail["billing"]; settledMinor: number; onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const [discountType, setDiscountType] = useState(billing.discountType);
  const [discountValue, setDiscountValue] = useState(String(billing.discountValue));
  const [taxPercent, setTaxPercent] = useState((billing.taxRateBps / 100).toFixed(2));
  const [reason, setReason] = useState("");
  const [allowBalance, setAllowBalance] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    const taxRateBps = Math.round(Number(taxPercent) * 100);
    const value = Number(discountValue);
    if (!Number.isInteger(value) || value < 0 || !Number.isInteger(taxRateBps) || taxRateBps < 0 || taxRateBps > 10_000) {
      toast("error", "Discount and tax must be valid non-negative values"); return;
    }
    setSaving(true);
    try {
      await api.post(`/api/v1/bookings/${bookingId}/billing-adjustment`, {
        discountType, discountValue: discountType === "NONE" ? 0 : value, taxRateBps, reason,
        ...(settledMinor !== 0 && allowBalance ? { balanceBehavior: "ALLOW_BALANCE" } : {}),
      });
      toast("success", "Billing snapshot updated");
      onSaved();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Billing adjustment failed");
    } finally { setSaving(false); }
  }

  return <Sheet open onClose={onClose} title="Adjust discount and tax" description="Creates an immutable billing snapshot. Percentage discounts use basis points (100 = 1%).">
    <form onSubmit={save} className="space-y-3">
      <div><Label>Discount type</Label><Select value={discountType} onChange={(event) => setDiscountType(event.target.value as typeof discountType)}><option value="NONE">None</option><option value="FIXED">Fixed minor units</option><option value="PERCENTAGE">Percentage basis points</option></Select></div>
      {discountType !== "NONE" && <div><Label>{discountType === "FIXED" ? "Discount (minor units)" : "Discount (basis points)"}</Label><Input required type="number" min="0" max={discountType === "PERCENTAGE" ? 10000 : 1000000000} value={discountValue} onChange={(event) => setDiscountValue(event.target.value)} /></div>}
      <div><Label>Tax rate (%)</Label><Input required inputMode="decimal" value={taxPercent} onChange={(event) => setTaxPercent(event.target.value)} /></div>
      <div><Label>Reason</Label><Textarea required minLength={3} maxLength={255} value={reason} onChange={(event) => setReason(event.target.value)} /></div>
      {settledMinor !== 0 && <label className="flex gap-2 rounded border p-3 text-sm"><input required type="checkbox" checked={allowBalance} onChange={(event) => setAllowBalance(event.target.checked)} /><span>I understand this preserves settled ledger entries and explicitly creates a remaining balance or customer credit.</span></label>}
      <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Create snapshot"}</Button></div>
    </form>
  </Sheet>;
}

function RecordTipSheet({ bookingId, currency, onClose, onSaved }: { bookingId: number; currency: string; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("CASH");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    let cents: number;
    try {
      cents = parseMajorToMinor(amount, currency);
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Enter a valid amount");
      return;
    }
    if (cents <= 0) {
      toast("error", "Enter an amount greater than zero.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/v1/bookings/${bookingId}/tip`, {
        amountCents: cents,
        method,
        notes: notes || null,
      });
      toast("success", "Tip recorded");
      onSaved();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onClose={onClose} title="Record tip / gratuity">
      <form onSubmit={save} className="space-y-3">
        <div>
          <Label>Amount ({currency})</Label>
          <Input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={minorDigitsFor(currency) === 0 ? "5" : "5.00"} />
        </div>
        <div>
          <Label>Method</Label>
          <Select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="CASH">CASH</option>
            <option value="CARD_OFFLINE">CARD (offline)</option>
            <option value="BANK_TRANSFER">BANK TRANSFER</option>
            <option value="WALLET">WALLET</option>
            <option value="OTHER">OTHER</option>
          </Select>
        </div>
        <div>
          <Label>Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="optional" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Record tip"}</Button>
        </div>
      </form>
    </Sheet>
  );
}
